import z from 'zod';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle, Divider, Grid } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  Button,
  SaveButton,
  SaveContextProvider,
  SaveHandler,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import { FieldValues, useFormContext } from 'react-hook-form';

import client from 'src/data/api';
import { CompanySubscriptionSetRecord } from 'src/types';
import { multilineIds, zodIssuesIntoErrorsMap } from 'src/utils/validation';
import { createDialogFormContext } from 'src/components/DialogFormContext';

export const {
  provider: DeleteCompanySubscriptionsDialogContextProvider,
  useContext: useDeleteCompanySubscriptionsDialogContext,
} = createDialogFormContext();

const CustomToolbar = ({ onCancel }: { onCancel: () => void }) => {
  const { reset: resetForm } = useFormContext();

  return (
    <Toolbar>
      <Grid container justifyContent="space-between">
        <Button label="Cancel" onClick={onCancel} />
        <SaveButton
          label="Delete"
          variant="contained"
          type="button"
          color="error"
          icon={<DeleteIcon />}
          mutationOptions={{
            onSuccess: () => resetForm(),
          }}
        />
      </Grid>
    </Toolbar>
  );
};

const validationSchema = z.object({ companyIds: multilineIds });

export const DeleteCompanySubscriptionsDialog = () => {
  const dialogCtx = useDeleteCompanySubscriptionsDialogContext();
  const companySubscriptionSetRecord = useRecordContext<CompanySubscriptionSetRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [deleteCompanySubscriptions] = useMutation<{
    delete_company_subscriptions: { returning: { id: string }[]; affected_rows: number };
  }>(
    gql`
      mutation delete_company_subscriptions($where: company_subscriptions_bool_exp!) {
        delete_company_subscriptions(where: $where) {
          returning {
            id
          }
          affected_rows
        }
      }
    `,
    { client, errorPolicy: 'all' },
  );

  if (companySubscriptionSetRecord?.all_companies) {
    // "All Companies" sets don't have explicit company subscriptions, so this component shouldn't
    // be rendered
    return null;
  }

  const handleSubmit: SaveHandler<FieldValues> = async (rawValues, callbacks) => {
    if (!companySubscriptionSetRecord) return undefined;

    const validationResult = validationSchema.safeParse(rawValues);
    if (!validationResult.success) return zodIssuesIntoErrorsMap(validationResult.error.issues);
    const values = validationResult.data;

    const { data, errors } = await deleteCompanySubscriptions({
      variables: {
        where: {
          company_id: { _in: values.companyIds },
          company_subscription_set_id: { _eq: companySubscriptionSetRecord.id },
        },
      },
    });

    if (errors) {
      console.error('Error deleting subscriptions:', errors);
      notify('There was a problem deleting the Company Subscriptions. Please try again.', { type: 'error' });
      return undefined;
    }

    const deletedCount = data?.delete_company_subscriptions.affected_rows ?? 0;

    if (deletedCount) {
      notify(`${deletedCount} Company Subscription${deletedCount > 1 ? 's have' : ' has'} been removed.`, {
        type: 'success',
      });
    } else {
      notify(`No subscriptions found for the given company ID(s).`, { type: 'info' });
    }

    if (callbacks?.onSuccess) callbacks.onSuccess();
    if (dialogCtx.onSuccess) dialogCtx.onSuccess();
    refresh();
    dialogCtx.setClosed();
    return undefined;
  };

  return (
    <Dialog open={dialogCtx.open} onClose={dialogCtx.setClosed} fullWidth>
      <DialogTitle>Remove Company Subscriptions</DialogTitle>
      <Divider />

      <DialogContent>
        <SaveContextProvider value={{ save: handleSubmit }}>
          <SimpleForm mode="onChange" toolbar={<CustomToolbar onCancel={dialogCtx.setClosed} />} mt={2} mx={1}>
            <TextInput
              label="Company ID(s)"
              source="companyIds"
              multiline
              validate={required()}
              fullWidth
              minRows={15}
              maxRows={15}
              autoFocus
            />
          </SimpleForm>
        </SaveContextProvider>
      </DialogContent>
    </Dialog>
  );
};
